import React, { useState } from "react";
import styled from "styled-components";
import mediaApi from "../../services/api/mediaApi";
import useAppSession from "../../stores/useAppSession";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 6px;
  width: 100%;
  max-width: 450px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  box-sizing: border-box;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 24px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 15px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #ff8f40;
  }

  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CancelButton = styled(Button)`
  background: #ffffff;
  border: 1px solid #eaeaea;
  color: #666;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

const ConfirmButton = styled(Button)`
  background: #ff8f40;
  border: 1px solid #ff8f40;
  color: #ffffff;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const YouTubeModal = ({ isOpen, onClose }) => {
  const [newUrl, setNewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const { sessionId, clientId } = useAppSession();

  if (!isOpen) return null;

  const isValidYouTubeUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname;
      const path = parsedUrl.pathname;

      const isYouTubeDomain =
        hostname === "www.youtube.com" ||
        hostname === "youtube.com" ||
        hostname === "m.youtube.com" ||
        hostname === "youtu.be";

      const isShorts = path.startsWith("/shorts");

      const isVideo =
        hostname === "youtu.be" ||
        parsedUrl.searchParams.has("v") ||
        path.startsWith("/watch");

      return isYouTubeDomain && !isShorts && isVideo;
    } catch {
      return false;
    }
  };

  const handleConfirm = async () => {
    if (newUrl.trim() === "") {
      toast.warning("Por favor, insira uma URL do YouTube.");
      return;
    }

    if (!isValidYouTubeUrl(newUrl)) {
      toast.error(
        "A URL fornecida não é válida ou não é compatível. Certifique-se de que seja um vídeo regular do YouTube."
      );
      return;
    }

    setLoading(true);
    try {
      await mediaApi.uploadYoutube({
        url: newUrl,
        sessionid: sessionId,
        client_id: clientId,
      });
      toast.success("Vídeo adicionado com sucesso!");
      setNewUrl("");
      onClose();
    } catch (error) {
      console.error("Erro ao fazer upload da URL do YouTube:", error);
      toast.error(
        "Ocorreu um erro ao adicionar o vídeo. Tente novamente mais tarde."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Adicionar URL do YouTube</ModalTitle>
          <CloseButton onClick={onClose} disabled={loading}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <InputGroup>
          <StyledInput
            type="text"
            placeholder="Insira a URL do vídeo..."
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            disabled={loading}
            autoFocus
          />
        </InputGroup>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={loading}>
            Cancelar
          </CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={loading}>
            {loading && <FontAwesomeIcon icon={faSpinner} spin />}
            Confirmar
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default YouTubeModal;